// Hybrid chat service - tries Pusher first, falls back to polling
import pusherService from './pusher';
import pollingChatService from './pollingChat';

class HybridChatService {
  constructor() {
    this.currentService = null;
    this.leadId = null;
    this.messageCallbacks = [];
    this.typingCallbacks = [];
    this.connectionCallbacks = [];
  }

  // Initialize chat service
  async initialize(leadId) {
    this.leadId = leadId;
    
    // Try Pusher first
    try {
      await this.initializePusher();
      console.log('Using Pusher for real-time chat');
    } catch (error) {
      console.warn('Pusher failed, falling back to polling:', error);
      await this.initializePolling();
      console.log('Using polling for chat');
    }
  }

  // Initialize Pusher
  async initializePusher() {
    const pusher = await pusherService.initialize();
    if (!pusher) {
      throw new Error('Pusher not available');
    }

    const channel = await pusherService.subscribeToLead(this.leadId);
    if (!channel) {
      throw new Error('Failed to subscribe to Pusher channel');
    }

    this.currentService = 'pusher';
    
    // Bind Pusher events
    channel.bind('new-message', (data) => {
      this.messageCallbacks.forEach(callback => callback([data.message]));
    });

    channel.bind('typing', (data) => {
      this.typingCallbacks.forEach(callback => callback(data));
    });

    // Bind connection events
    pusher.connection.bind('connected', () => {
      this.connectionCallbacks.forEach(callback => callback('connected'));
    });

    pusher.connection.bind('disconnected', () => {
      this.connectionCallbacks.forEach(callback => callback('disconnected'));
    });
  }

  // Initialize polling
  async initializePolling() {
    this.currentService = 'polling';
    pollingChatService.startPolling(this.leadId);
    
    // Bind polling events
    pollingChatService.onNewMessage((messages) => {
      this.messageCallbacks.forEach(callback => callback(messages));
    });

    pollingChatService.onTyping((data) => {
      this.typingCallbacks.forEach(callback => callback(data));
    });
  }

  // Send message
  async sendMessage(message) {
    try {
      const response = await fetch('/api/lead-messages', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          lead_id: this.leadId,
          message: message
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Send message error:', error);
      throw error;
    }
  }

  // Send typing indicator
  async sendTyping(isTyping) {
    if (this.currentService === 'pusher') {
      // Pusher typing is handled by backend
      await fetch('/api/chat/typing', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          lead_id: this.leadId,
          is_typing: isTyping
        })
      });
    } else if (this.currentService === 'polling') {
      await pollingChatService.sendTyping(this.leadId, isTyping);
    }
  }

  // Subscribe to new messages
  onNewMessage(callback) {
    this.messageCallbacks.push(callback);
  }

  // Subscribe to typing indicators
  onTyping(callback) {
    this.typingCallbacks.push(callback);
  }

  // Subscribe to connection events
  onConnectionChange(callback) {
    this.connectionCallbacks.push(callback);
  }

  // Get connection state
  getConnectionState() {
    if (this.currentService === 'pusher') {
      return pusherService.getConnectionState();
    } else if (this.currentService === 'polling') {
      return pollingChatService.getConnectionState();
    }
    return 'disconnected';
  }

  // Get current service type
  getServiceType() {
    return this.currentService;
  }

  // Disconnect
  disconnect() {
    if (this.currentService === 'pusher') {
      pusherService.clearAllSubscriptions();
    } else if (this.currentService === 'polling') {
      pollingChatService.stopPolling();
    }
    
    this.currentService = null;
    this.messageCallbacks = [];
    this.typingCallbacks = [];
    this.connectionCallbacks = [];
  }
}

// Create singleton instance
const hybridChatService = new HybridChatService();

export default hybridChatService;
